import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { change, Field, reduxForm } from 'redux-form';

import { Typography } from '@material-ui/core';

import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import HotelOutlinedIcon from '@material-ui/icons/HotelOutlined';

import { ACCOMMODATION, BOOK_DATES } from 'graphql/queries/accommodations';
import {
  CREATE_ACCOMMODATION_BOOKING,
  UPDATE_ACCOMMODATION_BOOKING
} from 'graphql/mutations/accommodations-bookings';

import {
  AppHeader,
  CircularLoader,
  FormActionButtons,
  BookInfoList,
  CustomDateRangePicker
} from 'components';

import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import { getReceivedAccommodationData } from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import { getParticipants } from 'utils/book-accommodation/prepareParticipants';
import { getPreparedBookAccommodationData } from 'utils/book-accommodation/prepareBookAccommodationData';
import {
  getAvailableRangeDates,
  getDisabledDates
} from 'utils/book-accommodation/availableDates';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import validate from './validate';

import useStyles from 'pages/book-accommodation/styles';

const initialValues = {
  maxParticipants: 0,
  currency: 'NOK',
  participants: [],
  addOns: [],
  activities: [],
  pay: {
    'Pay on arrival': true
  }
};

const FIXED_PRICES = 'pricesFixedForm';
const CATEGORY_PRICES = 'pricesCategoryForm';

const BookAccommodation = ({
  bookAccommodationForm,
  changeFormFieldValue,
  handleSubmit,
  bookedData,
  isUpdate,
  businessOwner
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const selectedLanguage = i18n.language;
  const ownerId = businessOwner.selectedBusinessOwner.id;

  const accommodationId = bookAccommodationForm.values.accommodationId;
  const initialDates = bookedData ? bookedData.dates : null;
  const startDate = initialDates ? initialDates.startDate : moment();

  const [isLoading, setLoading] = React.useState(null);
  const [accommodation, setAccommodation] = React.useState(null);
  const [isUpdatedParticipants, setIsUpdatedParticipants] = React.useState(
    false
  );

  const [createAccommodationBooking] = useMutation(
    CREATE_ACCOMMODATION_BOOKING
  );
  const [updateAccommodationBooking] = useMutation(
    UPDATE_ACCOMMODATION_BOOKING
  );
  const { loading, data } = useQuery(ACCOMMODATION, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationId || parseInt(id),
      businessOwnerId: ownerId
    }
  });
  const {
    loading: bookDatesLoading,
    data: bookDatesData,
    refetch: bookDatesRefetch
  } = useQuery(BOOK_DATES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationId || parseInt(id),
      businessOwnerId: ownerId,
      startPeriod: moment(startDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(startDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (data && data.accommodations && data.accommodations.length) {
      const receivedAccommodation = data.accommodations[0];
      const accommodationData = getReceivedAccommodationData(
        receivedAccommodation
      );

      if (receivedAccommodation.prices[0].participant.participant) {
        accommodationData['pricesType'] = CATEGORY_PRICES;
      } else {
        accommodationData['pricesType'] = FIXED_PRICES;
      }

      setListOfAvailableCurrencies(receivedAccommodation);
      setMaxParticipants(accommodationData);
      setParticipants(accommodationData);
      setAddOns(accommodationData);

      setAccommodation(accommodationData);
    }
  }, [data]);

  React.useEffect(() => {
    if (!bookAccommodationForm || !accommodation) return;
    setParticipants(accommodation);
  }, [bookAccommodationForm.values.currency]);

  const changeMonthHandler = date => {
    bookDatesRefetch({
      accommodationId: accommodationId || parseInt(id),
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const setListOfAvailableCurrencies = receivedAccommodation => {
    const listOfAvailableCurrencies = getListOfAvailableCurrencies(
      receivedAccommodation.prices
    );
    const selectedCurrency = bookAccommodationForm.values.currency;

    if (!listOfAvailableCurrencies.includes(selectedCurrency)) {
      changeFormFieldValue('currency', listOfAvailableCurrencies[0]);
    }
  };

  const setParticipants = accommodation => {
    setIsUpdatedParticipants(!isUpdatedParticipants);

    const currency = bookAccommodationForm.values.currency;
    const previousParticipants =
      bookAccommodationForm.values.participants || [];
    const participants = getParticipants(
      accommodation,
      currency,
      previousParticipants
    );

    changeFormFieldValue('participants', participants);
  };

  const setAddOns = accommodation => {
    const previousAddOns = bookAccommodationForm.values.addOns || [];

    const addOnsList = accommodation.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });

    changeFormFieldValue('addOns', addOnsList);
  };

  const setMaxParticipants = accommodation => {
    changeFormFieldValue('maxParticipants', accommodation.generalForm.guests);
  };

  const handlerSubmitWrapper = props => {
    if (bookAccommodationForm.syncErrors) {
      const syncErrors = bookAccommodationForm.syncErrors;
      const errorsLength = Object.keys(syncErrors).length;

      if (syncErrors.isMaxParticipantsErrors && errorsLength === 2) {
        const error = syncErrors.participants['_error'];
        const errorSplit = error.split(' ');
        const errorLength = errorSplit.length;
        enqueueSnackbar(
          t('The number of Participants can’t exceed', {
            number: errorSplit[errorLength - 1]
          }),
          {
            variant: 'warning'
          }
        );
      } else {
        enqueueSnackbar(t('Please fill in all the required fields'), {
          variant: 'warning'
        });
      }
    }

    handleSubmit(submit)(props);
  };

  const submit = () => {
    setLoading(true);

    if (
      bookAccommodationForm.values.participants &&
      bookAccommodationForm.values.participants.length
    ) {
      bookAccommodationForm.values.participants = [
        {
          ...bookAccommodationForm.values.participants[0],
          participants: bookAccommodationForm.values.maxParticipants
        }
      ];
    }

    let data = getPreparedBookAccommodationData(
      accommodation,
      bookAccommodationForm,
      []
    );

    if (isUpdate) {
      updateAccommodationBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(
            t('Accommodation booking has been successfully updated'),
            {
              variant: 'success'
            }
          );
          history.push({
            pathname: '/bookings-list'
          });
        },
        () => {
          setLoading(false);
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    } else {
      createAccommodationBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(t('Accommodation has been successfully booked'), {
            variant: 'success'
          });
          history.push({
            pathname: '/accommodations'
          });
        },
        () => {
          setLoading(false);
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
        }
      );
    }
  };

  const generalForm = accommodation
    ? getFormByCurrentLanguage(
        selectedLanguage,
        accommodation.generalForm,
        accommodation['generalForm-Norwegian']
      )
    : {};
  const descriptionForm = accommodation
    ? getFormByCurrentLanguage(
        selectedLanguage,
        accommodation.descriptionForm,
        accommodation['descriptionForm-Norwegian']
      )
    : {};

  return (
    <>
      <AppHeader />

      <CircularLoader
        isLoading={loading || bookDatesLoading || isLoading}
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      {accommodation && (
        <div className={classes.root}>
          <div className={classes.leftSide}>
            <Typography variant="h3" classes={{ root: classes.productCode }}>
              {t('Product code')}: {accommodation.generalForm.productCode}
            </Typography>

            <div
              className={classes.photo}
              style={{
                backgroundImage: `url(${accommodation.generalForm.coverPhoto.image})`
              }}
            >
              {''}
            </div>

            <Typography variant="h2" className={classes.fontWeight}>
              {generalForm.title}
            </Typography>

            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {accommodation.generalForm.location}
                </Typography>
              </div>
              <div className={classes.infoItem}>
                <PeopleOutlineIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {accommodation.generalForm.guests} {t('Guests')}
                </Typography>
              </div>
              {generalForm.bedDetails && !!generalForm.bedDetails.length && (
                <div className={classes.infoItem}>
                  <HotelOutlinedIcon classes={{ root: classes.infoIcon }} />
                  <Typography variant="body1">
                    {generalForm.bedDetails.join(' • ')}
                  </Typography>
                </div>
              )}
            </div>

            <Typography variant="body1" classes={{ root: classes.overview }}>
              {descriptionForm.overview}
            </Typography>

            <BookInfoList
              activity={accommodation}
              currency={bookAccommodationForm.values.currency}
              defaultExpanded={true}
            />
          </div>

          <div className={classes.rightSide}>
            <form
              onSubmit={handlerSubmitWrapper}
              className={classes.bookWrapper}
            >
              <Typography
                variant="h1"
                className={classNames(classes.fontWeight, classes.inputIndent)}
              >
                {isUpdate
                  ? t('Update Accommodation Booking')
                  : t('Book Accommodation')}
              </Typography>

              <Field
                name="dates"
                availableRange={getAvailableRangeDates(accommodation)}
                disabledDates={getDisabledDates(bookDatesData, initialDates)}
                changeMonthsCallback={changeMonthHandler}
                className={classes.inputIndent}
                inputClassName={classes.disableInputFocus}
                label="Select dates *"
                initialFocus="startDate"
                dateRangePickerClass="dateRangeOpenedPicker"
                daySize={39}
                component={CustomDateRangePicker}
                updateBooking
              />

              <FormActionButtons
                cancelPath={isUpdate ? '/bookings-list' : '/accommodations'}
                actionLabel={isUpdate ? 'Update' : 'Book'}
              />
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => {
  return {
    bookAccommodationForm: state.form.bookAccommodationForm,
    businessOwner: state.businessOwner
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookAccommodationForm', field, value));
  }
});

export default reduxForm({
  form: 'bookAccommodationForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(BookAccommodation));
